import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import PropertyCard from '@/components/PropertyCard';
import { supabase, type Property } from '@/lib/supabase';
import { ArrowRight } from 'lucide-react';
import { useSiteContent } from '@/hooks/useSiteContent';
import { AnimatedSection } from '@/hooks/useScrollAnimation';

const PropertyTeaser = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const { getContentText, loading: contentLoading } = useSiteContent();

  useEffect(() => {
    fetchFeaturedProperties();
  }, []);

  const fetchFeaturedProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('status', 'available')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setProperties(data || []);
    } catch (error) {
      console.error('Error fetching featured properties:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic grid classes based on number of items
  const getGridClasses = (count: number) => {
    if (count === 1) return 'grid grid-cols-1 max-w-md mx-auto';
    if (count === 2) return 'grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto';
    return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto';
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 xl:py-28 bg-background relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-12 lg:mb-16">
          <div className="divider-elegant mx-auto mb-4 lg:mb-6"></div>
          <h2 className="font-serif text-primary mb-4">
            {getContentText('property-teaser-title', 'Beschikbare Verhuur')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            {getContentText('property-teaser-description', 'Ontdek onze selectie van hoogwaardige woon- en bedrijfsruimtes')}
          </p>
        </AnimatedSection>

        {loading || contentLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Verhuur laden...</p>
          </div>
        ) : properties.length === 0 ? (
          <AnimatedSection className="text-center py-12">
            <p className="text-muted-foreground">Geen verhuurobjecten beschikbaar.</p>
          </AnimatedSection>
        ) : (
          <div className={`${getGridClasses(properties.length)} gap-8 mb-12`}>
            {properties.map((property, index) => (
              <AnimatedSection key={property.id} delay={index * 100}>
                <PropertyCard property={property} />
              </AnimatedSection>
            ))}
          </div>
        )}

        <AnimatedSection delay={400} className="text-center">
          <a href="/verhuur">
            <Button size="lg" className="btn-luxury group px-8 py-6 text-base rounded-lg">
              Bekijk Alle Verhuur
              <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </a>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default PropertyTeaser;
